import React, { useRef, useState } from "react";
import { MAX_GALLERY_IMAGES } from "../../utils/image/image-constants";
import { ALLOWED_RASTER_EXTENSIONS } from "../../utils/image/image-constants";
import { validateRasterImageFile } from "../../utils/image/image-file-utils";
import { sharedCompressImage } from "../../utils/shared-functions";
import { uploadRasterImage, deleteRemoteImageIfNeeded } from "../../utils/image/product-image-service";
import { toImageSrc } from "../../utils/ecommerce/svg-image-src";

function GalleryRasterItem({ url, onRemove, disabled, removeLabel }) {
    return (
        <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-slate-50 aspect-square">
            <img src={toImageSrc(url)} alt="" className="h-full w-full object-cover" />
            <button
                type="button"
                onClick={onRemove}
                disabled={disabled}
                className="absolute top-2 right-2 rounded-lg bg-white/95 px-2 py-1 text-xs font-semibold text-red-600 shadow border border-red-100 hover:bg-red-50 disabled:opacity-50"
            >
                {removeLabel}
            </button>
        </div>
    );
}

export default function GalleryRasterUpload({
    images = [],
    onChange,
    t,
    disabled = false,
    onBusyChange,
    onError,
}) {
    const inputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);

    const canAddMore = images.length < MAX_GALLERY_IMAGES;

    const uploadFile = async (file) => {
        const validation = validateRasterImageFile(file);
        if (!validation.valid) {
            onError?.(t(validation.key));
            return;
        }

        try {
            onBusyChange?.(true);
            const compressed = await sharedCompressImage(file);
            const uploadedUrl = await uploadRasterImage(file, compressed, t);
            onChange([...images, uploadedUrl]);
        } catch (err) {
            onError?.(err.message ?? t("alert_process_failed"));
        } finally {
            onBusyChange?.(false);
        }
    };

    const handleFiles = async (fileList) => {
        if (!canAddMore || !fileList?.length) return;
        const file = fileList[0];
        await uploadFile(file);
    };

    const handleRemove = async (index) => {
        const url = images[index];
        try {
            onBusyChange?.(true);
            await deleteRemoteImageIfNeeded(url, t);
            onChange(images.filter((_, i) => i !== index));
        } catch (err) {
            onError?.(err.message ?? t("alert_process_failed"));
        } finally {
            onBusyChange?.(false);
        }
    };

    return (
        <div>
            {canAddMore && (
                <div
                    className={`rounded-2xl border-2 border-dashed px-4 py-8 text-center transition ${
                        dragOver
                            ? "border-ct-primary-500 bg-ct-primary-50/40"
                            : "border-slate-300 bg-slate-50 hover:border-ct-primary-400 hover:bg-ct-primary-50/30"
                    }`}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={async (e) => {
                        e.preventDefault();
                        setDragOver(false);
                        if (disabled) return;
                        await handleFiles(e.dataTransfer.files);
                    }}
                >
                    <p className="text-sm font-semibold text-slate-700">{t("label_arrastrar_imagen")}</p>
                    <p className="text-xs text-slate-500 mt-1">{t("label_galeria_una_por_vez")}</p>
                    <p className="text-xs text-slate-500">{t("label_galeria_max_imagenes", { count: MAX_GALLERY_IMAGES })}</p>
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => inputRef.current?.click()}
                        className="mt-4 inline-flex items-center justify-center rounded-xl bg-ct-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-ct-primary-700 disabled:opacity-50"
                    >
                        {t("label_seleccionar_imagen")}
                    </button>
                    <input
                        ref={inputRef}
                        type="file"
                        accept={ALLOWED_RASTER_EXTENSIONS.join(",")}
                        className="sr-only"
                        disabled={disabled}
                        onChange={(e) => handleFiles(e.target.files)}
                    />
                </div>
            )}

            {images.length > 0 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    {images.map((url, index) => (
                        <GalleryRasterItem
                            key={`${url}-${index}`}
                            url={url}
                            disabled={disabled}
                            removeLabel={t("label_eliminar")}
                            onRemove={() => handleRemove(index)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
